import {
  Component,
  Input,
  OnChanges,
  OnInit,
  Pipe,
  PipeTransform,
  SimpleChanges,
} from '@angular/core';
import {
  ProjectDisplayObservableService,
  SignSelectedEvent,
} from 'src/app/services/project-display-observable.service';
import { SubscriptionWithDestroyTriggerComponent } from 'src/app/subscription-helpers/subscription-with-destroy-trigger.component';
import ProductHelper from 'src/app/helpers/product-helper';
import Board from 'src/app/model/product/board/board';
import IPBoard from 'src/app/model/product/ip-board/ip-board';
import OtherProduct from 'src/app/model/product/other-product/other-product';
import Product from 'src/app/model/product/product';
import { ProjectPlacement } from 'src/app/model/project/projectPlacement';
import {
  CLIENT_WINDOW_STICKER_TYPE,
  ClientWindowSticker,
} from 'src/app/model/product/windowsticker/client-windowsticker';
import CustomWindowsticker from 'src/app/model/product/windowsticker/custom-windowsticker';
import Windowsticker from 'src/app/model/product/windowsticker/windowsticker';
import { SignDetail } from 'src/app/model/sign/sign-detail';
import { SignHelper } from 'src/app/helpers/sign-helper';
import StandardStickerPart from 'src/app/model/product/windowsticker/standard-sticker-part';
import StickerPart from 'src/app/model/product/windowsticker/sticker-part';
import { CLIENT_STICKER_PART_TYPE } from 'src/app/model/product/windowsticker/client-sticker-part';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { BoardTypeChoices } from 'src/app/model/product/board/board-part';
import { StringHelper } from 'src/app/helpers/string-helper';
import { ClientIPBoardAndPlacementHelper } from 'src/app/shared/project-components/client-project-form/base-project/client-ip-board-form/helper/client-ip-board-and-placement-helper';
import { Router } from '@angular/router';
import {
  KeysOfType,
  UtilityFunctions,
} from '../../../../../../helpers/utility-functions';
import IPBoardPart from '../../../../../../model/product/ip-board/ip-board-part';

const getProperty = UtilityFunctions.getProperty;

@Component({
  selector: 'configuration-card',
  templateUrl: './configuration-card.component.html',
  styleUrls: ['./configuration-card.component.scss'],
})
export class ConfigurationCardComponent
  extends SubscriptionWithDestroyTriggerComponent
  implements OnInit, OnChanges
{
  @Input()
  products: Product[] = [];

  @Input()
  firstProduct: Product;

  @Input()
  collaborative: boolean = false;

  @Input()
  client: boolean = false;

  @Input()
  installerView: boolean = false;

  @Input()
  projectPlacement?: ProjectPlacement;

  title: string = '';

  showArchivedSignWarningLeft: boolean = false;
  showArchivedSignWarningRight: boolean = false;

  pipeTriggerCounter = 0;

  constructor(
    private projectDisplayObservable: ProjectDisplayObservableService,
    private router: Router
  ) {
    super();
  }

  ngOnInit(): void {
    this.title = this.getTitle();
    this.calculateShowArchivedSignWarning();
    this.initializeSubscriptions();
  }

  private initializeSubscriptions(): void {
    const pipeTriggerCounterObservable = this.createObservableUntilDestroy(
      this.projectDisplayObservable.getProductPartTypeChangedSubscribable(),
      this.projectDisplayObservable.getProductMaterialChangedSubscribable(),
      this.projectDisplayObservable.getProductPlacementVariableChangedSubscribable(),
      this.projectDisplayObservable.getStockInfoUpdatedSubscribable(),
      this.projectDisplayObservable.getIPBoardConfigurationChangedSubscribable()
    );
    const titleObservable = this.createObservableUntilDestroy(
      this.projectDisplayObservable.getProductAddedSubscribable(),
      this.projectDisplayObservable.getProductMaterialChangedSubscribable()
    );
    const showArchivedWarningObservable = this.createObservableUntilDestroy(
      this.projectDisplayObservable.getSignSelectedSubscribable(),
      this.projectDisplayObservable.getRightSignSelectedSubscribable()
    );
    this.addSubscription(
      pipeTriggerCounterObservable,
      () => this.pipeTriggerCounter++
    );
    this.addSubscription(titleObservable, () => {
      this.title = this.getTitle();
    });
    this.addSubscription(
      showArchivedWarningObservable,
      (event: SignSelectedEvent) => {
        if (this.signSelectedEventMatches(event)) {
          this.calculateShowArchivedSignWarning();
        }
      }
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['firstProduct'] || changes['collaborative']) {
      this.title = this.getTitle();
    }
  }

  getTitle() {
    if (this.firstProduct != null) {
      if (ProductHelper.isIPBoard(this.firstProduct)) {
        if (this.firstProduct.leftRightBoard) {
          return 'Links rechts bord';
        } else if (this.collaborative) {
          return 'Gezamenlijk bord';
        } else if (this.firstProduct.boardPart.sign != null) {
          return 'Voorraadbord';
        } else {
          return 'Custom bord';
        }
      } else if (
        ProductHelper.isWindowSticker(this.firstProduct) ||
        ProductHelper.isCustomWindowSticker(this.firstProduct) ||
        ProductHelper.isClientWindowSticker(this.firstProduct)
      ) {
        return 'Raamsticker';
      } else if (ProductHelper.isBoard(this.firstProduct)) {
        return 'Projectbord';
      } else if (ProductHelper.isOtherProduct(this.firstProduct)) {
        const otherProduct = <OtherProduct>this.firstProduct;
        return ProductHelper.getType(
          otherProduct.type,
          otherProduct.otherProductName
        );
      } else {
        return 'Andere';
      }
    }
    return 'Product';
  }

  private calculateShowArchivedSignWarning(): void {
    if (
      this.firstProduct != null &&
      ProductHelper.isIPBoard(this.firstProduct)
    ) {
      this.showArchivedSignWarningLeft =
        this.firstProduct.boardPart != null &&
        this.firstProduct.boardPart.sign != null &&
        this.firstProduct.boardPart.sign.archived;
      this.showArchivedSignWarningRight =
        this.firstProduct.leftRightBoard &&
        this.firstProduct.otherSideBoardPart != null &&
        this.firstProduct.otherSideBoardPart.sign != null &&
        this.firstProduct.otherSideBoardPart.sign.archived;
    } else {
      this.showArchivedSignWarningLeft = false;
      this.showArchivedSignWarningRight = false;
    }
  }

  private signSelectedEventMatches(event: SignSelectedEvent): boolean {
    return (
      event != null &&
      ProductHelper.isIPBoard(this.firstProduct) &&
      (this.firstProduct.boardPart === event.ipBoardPart ||
        (this.firstProduct.leftRightBoard &&
          this.firstProduct.otherSideBoardPart === event.ipBoardPart))
    );
  }

  getProductSize(product: Product) {
    let sizeArr = [];
    let boardPart = null;
    if (product.type === CLIENT_WINDOW_STICKER_TYPE) {
      sizeArr = (<ClientWindowSticker>product).clientStickerParts.map((p) => {
        if (p.partType === CLIENT_STICKER_PART_TYPE.STANDARD_STICKER_PART) {
          const instance = p.instance as StandardStickerPart;
          if (instance.productSize != null) {
            return (
              instance.productSize.height + 'x' + instance.productSize.width
            );
          }
        } else {
          const instance = p.instance as StickerPart;
          return instance.stickerHeight + 'x' + instance.stickerWidth;
        }
      });
    } else if (ProductHelper.isWindowSticker(product)) {
      sizeArr = (<Windowsticker>product).standardStickerParts.map((p) => {
        if (p.productSize != null) {
          return p.productSize.height + 'x' + p.productSize.width;
        }
      });
    } else if (ProductHelper.isCustomWindowSticker(product)) {
      sizeArr = (<CustomWindowsticker>product).stickerParts.map(
        (p) => p.stickerHeight + 'x' + p.stickerWidth
      );
    } else if (ProductHelper.isBoard(product)) {
      sizeArr = (<Board>product).boardParts.map(
        (p) => p.height + 'x' + p.width
      );
    } else if (ProductHelper.isIPBoard(product)) {
      boardPart = (<IPBoard>product).boardPart;
      if (boardPart.sign != null) {
        sizeArr = [
          SignHelper.getSignOrientationDisplay(
            boardPart.sign,
            false,
            false,
            false
          ),
        ];
      } else if (
        boardPart.boardType != null &&
        boardPart.boardType.ordinal !== BoardTypeChoices.DIVERGENT.ordinal
      ) {
        sizeArr = [boardPart.otherHeight + 'x' + boardPart.otherWidth];
      }
    } else {
      sizeArr = [
        (<OtherProduct>product).height + 'x' + (<OtherProduct>product).width,
      ];
    }
    return (sizeArr.length > 0 ? ' - ' : '') + sizeArr.join('<br>');
  }

  tryRouteToSign(product: Product, boardPart: keyof IPBoard): void {
    if (this.client || !ProductHelper.isIPBoard(product)) {
      return;
    }

    if (product[boardPart] != null && product[boardPart].sign != null) {
      this.router.navigateByUrl(
        `/signs-portal/signs/${product[boardPart].sign.id}`
      );
    }
  }
}

@Pipe({
  name: 'productPlacementConfig',
  pure: false,
})
export class ProductPlacementConfigPipe implements PipeTransform {
  public constructor() {}

  transform(product: Product, pipeTriggerCounter: number): string {
    if (ProductHelper.isBoard(product)) {
      if (!CollectionHelper.isPopulated((<Board>product).boardParts)) {
        return '-';
      }
      const boardPart = (<Board>product).boardParts[0];
      if (boardPart == null) {
        return '-';
      }
      const boardType =
        boardPart.boardType != null ? boardPart.boardType.label : '';
      const boardPlacement =
        boardPart.placementType != null ? boardPart.placementType.label : '';
      return boardType + ' - ' + boardPlacement;
    } else if (ProductHelper.isIPBoard(product)) {
      const boardPart = (<IPBoard>product).boardPart;
      if (boardPart == null) {
        return '-';
      }
      if (ClientIPBoardAndPlacementHelper.isAdhesiveBoard(boardPart)) {
        return boardPart.placementType != null
          ? boardPart.placementType.label
          : '';
      }
      const boardType =
        boardPart.boardType != null ? boardPart.boardType.label : '';
      let boardPlacement = null;
      if (
        boardPart.boardType != null &&
        boardPart.boardType.ordinal === BoardTypeChoices.DIVERGENT.ordinal
      ) {
        boardPlacement =
          boardPart.divergentBoardType != null
            ? boardPart.divergentBoardType
            : '';
      } else {
        boardPlacement =
          boardPart.placementType != null ? boardPart.placementType.label : '';
      }
      return boardType + ' - ' + boardPlacement;
    } else {
      return (<OtherProduct>product).otherProductMaterial;
    }
  }
}

@Pipe({
  name: 'productStockConfig',
})
export class ProductStockConfigPipe implements PipeTransform {
  public constructor() {}

  transform(
    product: Product,
    boardPart: keyof IPBoard,
    pipeTriggerCounter
  ): string {
    if (
      ProductHelper.isIPBoard(product) &&
      product[boardPart] != null &&
      product[boardPart].sign &&
      !product[boardPart].sign.withoutStock
    ) {
      const ipBoardPart = product[boardPart];
      let totalStock =
        ipBoardPart.sign['totalStock'] != null
          ? ipBoardPart.sign['totalStock']
          : 0;
      if (ipBoardPart.sign['orderedAmount'] != null) {
        totalStock += Number(ipBoardPart.sign['orderedAmount']);
      }
      let stockString = '(' + totalStock + ' stuks';
      if (
        ipBoardPart.sign['inProductionStock'] != null ||
        ipBoardPart.sign['orderedAmount'] != null ||
        ipBoardPart.sign['inOfferStock'] != null
      ) {
        const totalInOfferStock = this.getTotalInOfferStock(ipBoardPart.sign);
        if (
          ipBoardPart.sign['inProductionStock'] > 0 ||
          totalInOfferStock > 0
        ) {
          stockString = `${stockString}, & ${
            ipBoardPart.sign['inProductionStock'] > 0 && totalInOfferStock > 0
              ? ipBoardPart.sign['inProductionStock'] +
                ' in productie & ' +
                totalInOfferStock +
                ' in offerte'
              : ipBoardPart.sign['inProductionStock'] > 0
              ? ipBoardPart.sign['inProductionStock'] + ' in productie'
              : totalInOfferStock + ' in offerte'
          }`;
        }
      }
      return stockString + ')';
    }
    return '';
  }

  private getTotalInOfferStock(sign: SignDetail): number {
    let toReturn = 0;
    if (sign['inOfferStock'] != null) {
      toReturn += Number(sign['inOfferStock']);
    }
    if (sign['orderedAmount'] != null) {
      toReturn += Number(sign['orderedAmount']);
    }
    return toReturn;
  }
}

@Pipe({
  name: 'productTypeConfig',
})
export class ProductTypeConfigPipe implements PipeTransform {
  public constructor() {}

  transform(
    product: Product,
    boardPart: KeysOfType<IPBoard, IPBoardPart>,
    pipeTriggerCounter: number
  ): string {
    if (
      ProductHelper.isIPBoard(product) &&
      getProperty(product, boardPart) != null
    ) {
      const ipBoardPart = product[boardPart];
      const typeLabel =
        ipBoardPart != null &&
        ClientIPBoardAndPlacementHelper.isAdhesiveBoard(ipBoardPart)
          ? 'Kleefbord'
          : 'IP-bord';
      return StringHelper.join(
        [
          typeLabel,
          ipBoardPart.sign == null ? null : ipBoardPart.sign.title,
          ipBoardPart.sign == null ? null : ipBoardPart.sign.phoneNumber,
        ],
        ' - '
      );
    } else {
      return ProductHelper.getTypeOf(product);
    }
  }
}
